import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { green1, green2 } from '../../../../colors';
import { GoogleMapsService } from '../../../../../services/google-maps.service';
import { BuildingInsights, RoofSegmentSizeAndSunshineStats } from '../../../../models/maps/building-insights';
import { inOutAnimation } from '../../../../animations';
import { ToastService } from '../../../../../services/toast.service';
import { LoaderService } from '../../../../../services/loader.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'psc-calculator-map',
  templateUrl: './calculator-map.component.html',
  styleUrls: ['./calculator-map.component.scss'],
  animations: [
    inOutAnimation({}),
  ],
})
export class CalculatorMapComponent implements OnInit {
  @Input() orientation: number;

  @Input()
  set toggleDrawingMode(value: boolean) {
    this.drawingMode = value;
    if (!this.mapInstance) {
      return;
    }
    this.setDrawingMode();
  }

  @Output() locationSet: EventEmitter<google.maps.LatLngLiteral> = new EventEmitter<google.maps.LatLngLiteral>();
  /**
   * Emits when a Polygon is drawn on the map. Emits calculated area in meters squared of the area that is drawn.
   */
  @Output() areaSelected: EventEmitter<number> = new EventEmitter<number>();
  @Output() roofSegmentSelected: EventEmitter<RoofSegmentSizeAndSunshineStats> = new EventEmitter<RoofSegmentSizeAndSunshineStats>();

  private mapInstance: google.maps.Map;
  drawingMode: boolean = true;
  center: google.maps.LatLngLiteral = { lat: 45.8, lng: 15.97 };
  markerPosition: google.maps.LatLngLiteral;
  mapZoom = 13;
  mapOptions: google.maps.MapOptions = {
    tilt: 0,
    rotateControl: false,
    mapTypeId: google.maps.MapTypeId.HYBRID,
  };
  drawnPolygon: google.maps.Polygon | null;

  solarApiMarkers: google.maps.MarkerOptions[];
  buildingInsights?: BuildingInsights;
  selectedMarker: google.maps.MarkerOptions;
  selectedRoofSegment: RoofSegmentSizeAndSunshineStats | null;
  showSolarApiOption: boolean;

  mapState: GoogleMapState = GoogleMapState.Initial;
  addressValue: string;

  constructor(
    private googleMapsService: GoogleMapsService,
    private toastService: ToastService,
    private loaderService: LoaderService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
  }

  getUserLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          if (position) {
            const userLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
            this.setLocationAndFillAddress(userLocation);
            this.center = userLocation;
          }
        });
    }
  }


  /** google.maps.drawing.DrawingManager was removed from the Maps JS API; draw manually via click-to-add-vertex + finishDrawing(). */
  setDrawingMode() {
    if (this.drawingMode) {
      this.unselectRoof();
      this.beginPolygonDrawing();
    }
  }

  private beginPolygonDrawing() {
    if (this.drawnPolygon) {
      this.drawnPolygon.setMap(null);
    }
    this.drawnPolygon = new google.maps.Polygon({
      map: this.mapInstance,
      strokeColor: green2,
      fillColor: green1,
      editable: false,
    });
  }

  onMapClick(latLng: google.maps.LatLngLiteral | undefined) {
    if (this.mapState === GoogleMapState.RoofSelection && this.drawnPolygon) {
      this.addDrawingPoint(latLng);
      return;
    }
    this.setLocationAndFillAddress(latLng);
  }

  addDrawingPoint(latLng: google.maps.LatLngLiteral | undefined) {
    if (!latLng || !this.drawnPolygon) return;
    this.drawnPolygon.getPath().push(new google.maps.LatLng(latLng));
    this.changeDetectorRef.detectChanges();
  }

  get canFinishDrawing(): boolean {
    return this.mapState === GoogleMapState.RoofSelection && !!this.drawnPolygon && this.drawnPolygon.getPath().getLength() >= 3;
  }

  finishDrawing() {
    if (!this.canFinishDrawing || !this.drawnPolygon) return;

    const polygon = this.drawnPolygon;
    this.mapState = GoogleMapState.RoofSelected;
    polygon.setOptions({ editable: true });

    this.setLocationFromPolygon(this.computeCentroid(polygon.getPath().getArray()), true);
    this.calculateAndEmitPolygonArea(polygon);
    this.calculateOnEvent('set_at', polygon);
    this.calculateOnEvent('insert_at', polygon);
    this.toggleDrawingMode = false;
    this.changeDetectorRef.detectChanges();
  }

  calculateOnEvent(event: string, polygon: google.maps.Polygon) {
    google.maps.event.addListener(polygon.getPath(), event, () => {
      this.calculateAndEmitPolygonArea(polygon);
      this.setLocationFromPolygon(this.computeCentroid(polygon.getPath().getArray()), false);
    });
  }

  calculateAndEmitPolygonArea(polygon: google.maps.Polygon) {
    this.areaSelected.emit(google.maps.geometry.spherical.computeArea(polygon.getPath()));
  }

  setLocation(location: google.maps.LatLngLiteral | undefined) {
    if (!location || this.mapState !== GoogleMapState.Initial) {
      return;
    }
    this.markerPosition = location;
    this.locationSet.emit(this.markerPosition);
  }

  private setLocationFromPolygon(location: google.maps.LatLngLiteral | undefined, fillAddress: boolean) {
    if (!location) {
      return;
    }
    this.markerPosition = location;
    this.locationSet.emit(this.markerPosition);
    if (fillAddress) {
      this.googleMapsService.getAddressFromLatLngPromise(location).subscribe(
        (res) => {
          if (res?.length > 0) {
            this.addressValue = res[0].formatted_address;
          }
        });
    }
  }

  setLocationAndFillAddress(location: google.maps.LatLngLiteral | undefined) {
    if (!location || this.mapState !== GoogleMapState.Initial) {
      return;
    }

    this.googleMapsService.getAddressFromLatLngPromise(location).subscribe(
      (res) => {
        if (res?.length > 0) {
          this.addressValue = res[0].formatted_address;
        }
      });
    this.setLocation(location);
  }

  mapInitialized(map: google.maps.Map) {
    this.mapInstance = map;
    this.setDrawingMode();
  }

  getSolarData(location: google.maps.LatLngLiteral) {
    this.loaderService.showLoader();
    this.googleMapsService.getSolarBuildingInsights(location)
      .pipe(finalize(() => this.loaderService.hideLoader()))
      .subscribe(
        {
          next: (buildingInsights) => {
            this.buildingInsights = buildingInsights as BuildingInsights;
            this.solarApiMarkers = buildingInsights.solarPotential.roofSegmentStats.map((stat) => this.roofSegmentToMarker(stat));
            this.markerPosition = {
              lng: this.buildingInsights.center.longitude,
              lat: this.buildingInsights.center.latitude,
            };
            this.mapState = GoogleMapState.SolarApiFound;
            this.showSolarApiOption = true;
          },
          error: err => {
            this.showSolarApiOption = false;
            this.mapState = GoogleMapState.SolarApiNotFound;
          },
        });
  }

  private roofSegmentToMarker(stat: RoofSegmentSizeAndSunshineStats) {
    const marker: google.maps.MarkerOptions = {
      position: { lat: stat.center.latitude, lng: stat.center.longitude },
      icon: this.getRoofSegmentIcon(),
    };
    return marker;
  }

  confirmLocation() {
    this.center = this.markerPosition;
    this.mapZoom = this.mapZoom === 20 ? 21 : 20;
    this.mapState = GoogleMapState.LocationConfirmed;
  }

  solarApiMarkerClicked(marker: google.maps.MarkerOptions, index: number) {
    this.selectedRoofSegment = (this.buildingInsights as BuildingInsights).solarPotential.roofSegmentStats[index];
    if (this.selectedMarker) {
      this.selectedMarker.icon = this.getRoofSegmentIcon();
    }
    this.selectedMarker = marker;
    marker.icon = this.getRoofSegmentIcon(35);
  }

  getRoofSegmentIcon(size: number = 30) {
    return {
      url: "assets/images/roof_icon.png",
      scaledSize: new google.maps.Size(size, size),
    };
  }

  selectCurrentSegment() {
    this.roofSegmentSelected.emit(this.selectedRoofSegment as RoofSegmentSizeAndSunshineStats);
  }

  computeCentroid(points: google.maps.LatLng[]): google.maps.LatLngLiteral {
    let latitude: number = 0;
    let longitude: number = 0;
    for (const point of points) {
      latitude += point.lat();
      longitude += point.lng();
    }

    const n: number = points.length;
    return { lat: latitude / n, lng: longitude / n };
  }

  unselectRoof() {
    this.toggleDrawingMode = false;
    this.drawnPolygon?.setMap(null);
    this.drawnPolygon = null;
    this.mapState = GoogleMapState.LocationConfirmed;
  }

  protected readonly GoogleMapState = GoogleMapState;

  startRoofSelection() {
    this.toggleDrawingMode = true;
    this.mapState = GoogleMapState.RoofSelection;
  }

  closeSegment() {
    this.selectedRoofSegment = null;
    this.selectedMarker.icon = this.getRoofSegmentIcon();
  }

  stopSegmentSelection() {
    this.buildingInsights = undefined;
    this.solarApiMarkers = [];
  }

  startFromBeginning() {
    this.unselectRoof();
    this.stopSegmentSelection();
    this.mapState = GoogleMapState.Initial;
    this.showSolarApiOption = true;
  }

  cancelSolarDataSelect() {
    this.stopSegmentSelection();
    this.mapState = GoogleMapState.LocationConfirmed;
  }

  setAddress(location: google.maps.places.PlaceResult) {
    this.setLocation(location.geometry?.location?.toJSON() || this.markerPosition);
    this.center = this.markerPosition;
    this.mapZoom = 21;
    this.changeDetectorRef.detectChanges();
  }
}

export enum GoogleMapState {
  Initial,
  RoofSelection,
  RoofSelected,
  LocationConfirmed,
  SolarApiFound,
  SolarApiNotFound,
}
